import { EnterpriseSsoProvider, SsoProvider } from '@neo.tax/shared';

import { EnterpriseSsoProviderLookup } from '../../hooks/useEnterpriseSsoProviderLookup';

export type EnterpriseSsoProviderIdValidation = { valid: true; providerId: string } | { valid: false; message: string };

const PROVIDER_TYPE_LABELS: Record<EnterpriseSsoProvider, string> = {
  [SsoProvider.OIDC]: 'OIDC',
  [SsoProvider.SAML]: 'SAML',
};

export const normalizeEnterpriseSsoProviderIdSuffix = (input: string): string => input.trim();

/**
 * Checks a typed provider ID before sign-in starts, so a mistake gets advice about the ID instead of a failed
 * sign-in. While the lookup of the ID may still answer, the ID is held back; once the lookup is inconclusive, it is
 * accepted and left to Identity Platform to judge.
 */
export const validateEnterpriseSsoProviderId = ({
  input,
  lookup,
  selectedType,
}: {
  input: string;
  selectedType: EnterpriseSsoProvider;
  lookup: EnterpriseSsoProviderLookup;
}): EnterpriseSsoProviderIdValidation => {
  const providerIdSuffix = normalizeEnterpriseSsoProviderIdSuffix(input);
  if (!providerIdSuffix) {
    return { valid: false, message: 'Enter your organization’s provider ID.' };
  }
  if (providerIdSuffix.includes('@')) {
    return {
      valid: false,
      message:
        'That is an email address. Enter your organization’s provider ID instead, which your IT administrator can give you.',
    };
  }

  if (lookup.status === 'debouncing' || lookup.status === 'checking') {
    return { valid: false, message: 'Still checking your organization’s provider ID. Try again in a moment.' };
  }
  const providerId = `${selectedType.toLowerCase()}.${providerIdSuffix}`;
  if (lookup.status === 'inconclusive' || lookup.configuredTypes.has(selectedType)) {
    return { valid: true, providerId };
  }

  const [configuredType] = [...lookup.configuredTypes];
  if (configuredType) {
    return {
      valid: false,
      message: `Your organization uses ${PROVIDER_TYPE_LABELS[configuredType]}, not ${PROVIDER_TYPE_LABELS[selectedType]}. Select ${PROVIDER_TYPE_LABELS[configuredType]} and try again.`,
    };
  }
  return {
    valid: false,
    message: `We don’t recognise the provider ID “${providerId}”. Check it with your IT administrator and try again.`,
  };
};
